import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { CameraMode } from "../types.ts";

const TRANSITION_MS = 600;
const EASING = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease-in-out quad

export interface CameraPreset {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov?: number;
  isOrtho?: boolean;
}

export class CameraManager {
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private currentMode: CameraMode = "dollhouse";
  private modelSize = new THREE.Vector3(10, 3, 10);

  // Walkthrough state
  private walkActive = false;
  private keys = new Set<string>();
  private pointerLocked = false;
  private yaw = 0;
  private pitch = 0;
  private readonly walkHeight = 1.65;

  constructor(camera: THREE.PerspectiveCamera, controls: OrbitControls) {
    this.camera = camera;
    this.controls = controls;
  }

  setModelSize(size: THREE.Vector3): void {
    this.modelSize = size.clone();
  }

  getMode(): CameraMode {
    return this.currentMode;
  }

  /** Smoothly transition to a new camera mode. */
  async setMode(mode: CameraMode): Promise<void> {
    if (mode === this.currentMode) return;
    this._exitCurrentMode();
    this.currentMode = mode;
    const preset = this._presetFor(mode);
    await this._animateTo(preset);
    this._enterCurrentMode();
  }

  /** Call from render loop — handles walkthrough WASD movement. */
  update(delta: number): void {
    if (this.currentMode !== "walkthrough" || !this.walkActive) return;
    this._updateWalkthrough(delta);
  }

  dispose(): void {
    this._exitCurrentMode();
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("keyup", this._onKeyUp);
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private _presetFor(mode: CameraMode): CameraPreset {
    const { x: sx, y: sy, z: sz } = this.modelSize;
    const maxH = Math.max(sx, sz);

    switch (mode) {
      case "dollhouse":
        return {
          position: new THREE.Vector3(0, sy * 1.4 + maxH * 0.5, maxH * 0.9),
          target: new THREE.Vector3(0, 0, 0),
          fov: 45,
        };
      case "walkthrough":
        return {
          position: new THREE.Vector3(0, this.walkHeight, sz * 0.3),
          target: new THREE.Vector3(0, this.walkHeight, 0),
          fov: 75,
        };
      case "floorplan":
        return {
          position: new THREE.Vector3(0, sy * 2 + maxH, 0),
          target: new THREE.Vector3(0, 0, 0),
          fov: 60,
        };
    }
  }

  private _enterCurrentMode(): void {
    if (this.currentMode === "walkthrough") {
      this.controls.enabled = false;
      this.walkActive = true;
      window.addEventListener("keydown", this._onKeyDown);
      window.addEventListener("keyup", this._onKeyUp);
      // Yaw from current camera direction
      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      this.yaw = Math.atan2(-dir.x, -dir.z);
      this.pitch = 0;
    } else {
      this.controls.enabled = true;
      this.walkActive = false;
      window.removeEventListener("keydown", this._onKeyDown);
      window.removeEventListener("keyup", this._onKeyUp);
      if (this.pointerLocked) document.exitPointerLock();
    }

    if (this.currentMode === "floorplan") {
      this.controls.enableRotate = false;
      this.controls.enablePan = true;
      this.controls.maxPolarAngle = 0.01; // top-down lock
    } else {
      this.controls.enableRotate = true;
      this.controls.maxPolarAngle = Math.PI / 1.8;
    }
  }

  private _exitCurrentMode(): void {
    this.walkActive = false;
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("keyup", this._onKeyUp);
    this.keys.clear();
    if (this.pointerLocked) document.exitPointerLock();
  }

  private _animateTo(preset: CameraPreset): Promise<void> {
    return new Promise((resolve) => {
      const startPos = this.camera.position.clone();
      const startTarget = this.controls.target.clone();
      const startFov = this.camera.fov;
      const endFov = preset.fov ?? startFov;
      const start = performance.now();

      const tick = () => {
        const elapsed = performance.now() - start;
        const raw = Math.min(elapsed / TRANSITION_MS, 1);
        const t = EASING(raw);

        this.camera.position.lerpVectors(startPos, preset.position, t);
        this.controls.target.lerpVectors(startTarget, preset.target, t);
        this.camera.fov = startFov + (endFov - startFov) * t;
        this.camera.updateProjectionMatrix();
        this.controls.update();

        if (raw < 1) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });
  }

  private _updateWalkthrough(delta: number): void {
    const SPEED = 3.5; // m/s
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const move = new THREE.Vector3();

    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) move.add(forward);
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) move.sub(forward);
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) move.sub(right);
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) move.add(right);

    if (move.lengthSq() > 0) {
      move.normalize().multiplyScalar(SPEED * delta);
      this.camera.position.add(move);
      this.camera.position.y = this.walkHeight; // keep at eye level
    }

    // Apply look direction from yaw+pitch
    const dir = new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch),
    );
    this.camera.lookAt(this.camera.position.clone().add(dir));
  }

  /** Call from canvas mousemove when pointer is locked. */
  applyPointerDelta(dx: number, dy: number): void {
    if (this.currentMode !== "walkthrough") return;
    const SENSITIVITY = 0.002;
    this.yaw -= dx * SENSITIVITY;
    this.pitch -= dy * SENSITIVITY;
    this.pitch = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, this.pitch));
  }

  private _onKeyDown = (e: KeyboardEvent) => { this.keys.add(e.code); };
  private _onKeyUp = (e: KeyboardEvent) => { this.keys.delete(e.code); };
}
